/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useErrorState } from '../../shared/useErrorState';
import { VStack } from 'chakra-v3';
import type { UserAudioComponent } from '@karya/core';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedParams, getTypedComponent } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import { ChatBubble } from '../../shared/ChatBubble';
import { Button } from '../../../../../components/buttons/Index';
import {
  MediaDeviceError,
  MinRecordingLengthError,
  MaxRecordingLengthError,
  RecordingError,
  FileSizeExceededError,
} from './ErrorMessages';
import { useNext } from '../../../hooks/useNext';

export const UserAudio: React.FC<ComponentRendererProps> = ({ node, isCurrent, controller }) => {
  const params = getTypedParams<UserAudioComponent>(node);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [error, setError] = useErrorState();
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);
  const validationPassedRef = useRef<boolean>(true);

  const autoPlay = params.autoPlay ?? false;
  const minRecordingLength = params.min_recording_length;
  const maxRecordingLength = params.max_recording_length;

  const getErrorMessage = (error: unknown): Error => {
    return new MediaDeviceError(error, 'microphone');
  };

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    // Capture recordingUrl at effect time (keep as dependency)
    const currentRecordingUrl = recordingUrl;

    return () => {
      // Access refs directly inside cleanup to get latest values
      const currentMediaRecorder = mediaRecorderRef.current;
      const currentStream = streamRef.current;

      // Stop media recorder if still recording
      if (currentMediaRecorder && currentMediaRecorder.state !== 'inactive') {
        try {
          currentMediaRecorder.stop();
        } catch (error) {
          console.error('Error stopping media recorder on cleanup:', error);
        }
      }

      // Stop all media stream tracks
      if (currentStream) {
        currentStream.getTracks().forEach((track) => {
          track.stop();
          currentStream.removeTrack(track);
        });
        streamRef.current = null;
      }

      // Revoke object URLs to prevent memory leaks
      if (currentRecordingUrl) {
        URL.revokeObjectURL(currentRecordingUrl);
      }
    };
  }, [recordingUrl]);

  const teardownFn = useCallback(
    (next: () => void) => {
      if (!recordingUrl) {
        return;
      }

      // Final validation before submission
      if (minRecordingLength !== undefined && duration < minRecordingLength) {
        setError(() => new MinRecordingLengthError(minRecordingLength, duration));
        return;
      }
      if (maxRecordingLength !== undefined && duration > maxRecordingLength) {
        setError(() => new MaxRecordingLengthError(maxRecordingLength, duration));
        return;
      }
      // Validate file size (reasonable default: 50MB for audio)
      const audioBlob =
        audioChunksRef.current.length > 0 ? new Blob(audioChunksRef.current, { type: 'audio/webm' }) : null;
      if (audioBlob) {
        const maxFileSize = 50 * 1024 * 1024; // 50MB
        if (audioBlob.size > maxFileSize) {
          const fileSizeMB = audioBlob.size / (1024 * 1024);
          setError(() => new FileSizeExceededError(fileSizeMB, 50));
          return;
        }
      }

      // Write outputs - recording and duration are output params for UserAudioComponent
      const component = getTypedComponent<UserAudioComponent>(node);
      controller.writeOutput(component.recording, recordingUrl);
      controller.writeOutput(component.duration, duration);

      // Only call next() when validation passes and output is written
      next();
    },
    [recordingUrl, minRecordingLength, maxRecordingLength, duration, node, controller, setError],
  );

  useNext({
    controller,
    teardownFn,
    strategy: 'MANUAL',
    isCurrent,
  });

  // Real-time duration update while recording
  useEffect(() => {
    if (!isRecording) {
      return;
    }

    const intervalId = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setDuration(elapsed);

      // Auto-stop when maxRecordingLength is reached
      if (maxRecordingLength !== undefined && elapsed >= maxRecordingLength) {
        // Clear interval first to prevent multiple calls
        clearInterval(intervalId);
        // Call stopRecording to trigger cleanup
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          validationPassedRef.current = true;
          mediaRecorderRef.current.stop();
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
          }
          setIsRecording(false);
          setDuration(maxRecordingLength);
        }
      }
    }, 500); // Update every 500ms for smooth UI updates

    return () => {
      clearInterval(intervalId);
    };
  }, [isRecording, maxRecordingLength]);

  const startRecording = async () => {
    try {
      setError(null);
      validationPassedRef.current = true; // Reset validation flag

      // Clean up any existing stream before creating a new one
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Revoke old recording URL if it exists
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
        setRecordingUrl(null);
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Only create URL if validation passed
        if (!validationPassedRef.current) {
          return;
        }
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        // Revoke old URL before creating new one
        if (recordingUrl) {
          URL.revokeObjectURL(recordingUrl);
        }
        const url = URL.createObjectURL(audioBlob);
        setRecordingUrl(url);

        if (autoPlay && typeof window !== 'undefined') {
          const audio = new window.Audio(url);
          void audio.play().catch((error) => {
            console.error('Error playing audio:', error);
          });
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        validationPassedRef.current = false;
        setError(() => new RecordingError());
        setIsRecording(false);
        // Clean up stream on error
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setDuration(0);
      startTimeRef.current = Date.now();
    } catch (error) {
      console.error('Error starting recording:', error);
      setError(() => getErrorMessage(error));
      // Clean up stream if it was created before error
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      validationPassedRef.current = true; // Reset validation flag
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      setIsRecording(false);
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setDuration(elapsed);

      // Validate duration limits
      if (minRecordingLength !== undefined && elapsed < minRecordingLength) {
        validationPassedRef.current = false;
        setError(() => new MinRecordingLengthError(minRecordingLength, elapsed));
        // Revoke URL if it was created before validation failed
        if (recordingUrl) {
          URL.revokeObjectURL(recordingUrl);
        }
        setRecordingUrl(null);
        return;
      }
      if (maxRecordingLength !== undefined && elapsed > maxRecordingLength) {
        validationPassedRef.current = false;
        setError(() => new MaxRecordingLengthError(maxRecordingLength, elapsed));
        // Revoke URL if it was created before validation failed
        if (recordingUrl) {
          URL.revokeObjectURL(recordingUrl);
        }
        setRecordingUrl(null);
        return;
      }
    }
  };

  return (
    <ChatBubble
      containerProps={{ w: 'fit-content', minW: { base: '200px', md: '300px' }, maxW: '70vw', alignSelf: 'flex-end' }}
    >
      <VStack gap="16px" align="stretch">
        <Text variant="body">Audio Recording Component</Text>
        {error && (
          <Text variant="body" color="error.solid">
            {error.message}
          </Text>
        )}
        {!isRecording && !recordingUrl && (
          <Button
            onClick={() => {
              void startRecording();
            }}
            variant="secondary"
            disabled={!isCurrent}
          >
            Start Recording
          </Button>
        )}
        {isRecording && (
          <VStack gap="12px">
            <Text variant="body">
              Recording... {duration}s
              {minRecordingLength && duration < minRecordingLength && (
                <Text as="span" color="error.solid">
                  {' '}
                  (min: {minRecordingLength}s)
                </Text>
              )}
              {maxRecordingLength && duration >= maxRecordingLength && (
                <Text as="span" color="error.solid">
                  {' '}
                  (max: {maxRecordingLength}s reached)
                </Text>
              )}
            </Text>
            <Button
              onClick={stopRecording}
              variant="secondary"
              disabled={minRecordingLength !== undefined && duration < minRecordingLength}
            >
              {minRecordingLength !== undefined && duration < minRecordingLength
                ? `Record at least ${minRecordingLength}s`
                : 'Stop Recording'}
            </Button>
          </VStack>
        )}
        {recordingUrl && !isRecording && (
          <VStack gap="12px">
            <audio src={recordingUrl} controls />
            <Text variant="caption">Duration: {duration}s</Text>
          </VStack>
        )}
      </VStack>
    </ChatBubble>
  );
};
